/**
 * DAL'ın giriş hatalarını anlamlı bir türe çevirir.
 *
 * Uç, hatayı düz Türkçe bir cümle olarak döndürüyor (`LoginService.AuthenticateAsync`
 * içindeki `ArgumentException` metinleri). Giriş ekranı bugüne kadar o cümleyi olduğu
 * gibi basıyordu: dili seçilmiş arayüzde Türkçe bir sunucu metni çıkıyor, üstelik
 * "Kullanıcı lisanslı değil." gibi bir durumda kullanıcı ne yapacağını bilmiyordu —
 * şifresini mi yanlış yazdı, hesabı mı kapalı?
 *
 * Tür burada belirlenir, metin arayüzde çevrilir; sunucu metni SON ÇARE olarak kalır
 * (tanınmayan bir hata sessizce "bir şeyler ters gitti"ye dönüşmesin).
 */
export type LoginErrorKind =
  'badCredentials' | 'unknownUser' | 'inactive' | 'noLicense' | 'noSeat' | 'network' | 'unknown';

export interface LoginErrorInfo {
  kind: LoginErrorKind;
  /** Sunucudan gelen özgün metin — tanınmayan hatada gösterilir. */
  raw: string;
}

const PATTERNS: Array<[LoginErrorKind, RegExp]> = [
  // Lisans kalıpları ÖNCE: "boş kullanıcı lisansı yok" cümlesi hem lisans hem kontenjan
  // sözcüğünü taşıyor, sıra bozulursa yanlış türe düşer.
  ['noSeat', /boş kullanıcı lisansı|kontenjan|no available (user )?license|seat/i],
  ['noLicense', /lisanslı değil|lisansı yok|not licensed|no license/i],
  ['inactive', /aktif değil|not active|disabled/i],
  ['unknownUser', /kullanıcı bulunamadı|user not found/i],
  ['badCredentials', /şifre hatalı|wrong password|invalid (user|credential)|kullanıcı kodu veya şifre/i],
];

export function classifyLoginError(message: unknown): LoginErrorInfo {
  const raw =
    message instanceof Error
      ? message.message
      : typeof message === 'string'
        ? message
        : String(message ?? '');
  if (/network|fetch|timeout|bağlan|ulaşılam|ECONNREFUSED/i.test(raw)) return { kind: 'network', raw };
  for (const [kind, re] of PATTERNS) {
    if (re.test(raw)) return { kind, raw };
  }
  return { kind: 'unknown', raw };
}

/** Bu hata kullanıcının kendi başına çözebileceği bir şey mi (şifreyi düzelt) yoksa yönetici işi mi? */
export function needsAdmin(kind: LoginErrorKind): boolean {
  return kind === 'noLicense' || kind === 'noSeat' || kind === 'inactive';
}
